import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Check, ArrowLeft, ExternalLink, Wallet, Award, Crown, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PublicWorktopQuote() {
  const navigate = useNavigate();
  const { companySlug } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedQuoteId, setSubmittedQuoteId] = useState(null);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [quoteType, setQuoteType] = useState('instant'); // 'instant' or '24hour'
  const [materials, setMaterials] = useState([]);
  const [materialType, setMaterialType] = useState(''); // 'granite', 'quartz', 'marble', etc.
  const [budgetTier, setBudgetTier] = useState(''); // 'best-price', 'mid', 'premium'
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [availableMaterialTypes, setAvailableMaterialTypes] = useState([]);
  const [availableBudgetTiers, setAvailableBudgetTiers] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [instantPrice, setInstantPrice] = useState(0);
  const [settings, setSettings] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [registerData, setRegisterData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    stone_colour: '',
    notes: ''
  });
  
  const [globalThickness, setGlobalThickness] = useState('20mm');
  
  const [pieces, setPieces] = useState([{
    name: 'Worktop 1',
    width_mm: '',
    height_mm: '',
    material: 'Quartz',
    edge_profile: 'Square Edge',
    edges: { front: false, left: false, right: false, back: false }
  }]);

  const [serviceOptions, setServiceOptions] = useState({
    include_templating: true,
    include_fitting: true,
    delivery_only: false,
    supply_only: false
  });

  const [extras, setExtras] = useState({
    undermount_sink: false,
    undermount_sink_width: '',
    undermount_sink_height: '',
    overmount_sink: false,
    overmount_sink_width: '',
    overmount_sink_height: '',
    hob_cutout: false,
    hob_cutout_width: '',
    hob_cutout_height: '',
    tap_hole: false,
    drainer_grooves: false,
    socket_cutout: false,
    socket_cutout_quantity: '1'
  });

  React.useEffect(() => {
    fetchCompanyInfo();
    fetchMaterials();
    fetchSettings();
  }, [companySlug]);
  
  const fetchCompanyInfo = async () => {
    try {
      // Use company slug from URL or default to 'set-in-stone'
      const slug = companySlug || 'set-in-stone';
      const response = await axios.get(`${API}/public/company/${slug}`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Failed to load company info, using defaults');
      // Set default company info
      setCompanyInfo({
        name: 'Stone Worktop Specialists',
        email: 'info@example.com',
        slug: companySlug || 'set-in-stone'
      });
    }
  };

  const fetchMaterials = async () => {
    try {
      const slug = companySlug || 'set-in-stone';
      const response = await axios.get(`${API}/public/company/${slug}/materials?in_stock_only=true`);
      setMaterials(response.data);
      setFilteredMaterials(response.data);
    } catch (error) {
      console.error('Failed to load materials');
    }
  };

  // Filter materials by material type and budget tier
  React.useEffect(() => {
    let filtered = materials;
    
    if (materialType) {
      filtered = filtered.filter(m => (m.material_type || 'granite') === materialType);
    }
    
    if (budgetTier) {
      filtered = filtered.filter(m => m.budget_tier === budgetTier);
    }
    
    setFilteredMaterials(filtered);
    setSelectedMaterial(null); // Reset selection when filters change
  }, [materialType, budgetTier, materials]);

  // Compute available material types and budget tiers from materials data
  React.useEffect(() => {
    if (materials.length > 0) {
      // Get unique material types that have at least one material
      const types = [...new Set(materials.map(m => m.material_type || 'granite'))];
      setAvailableMaterialTypes(types);
      
      // Get unique budget tiers that have at least one material
      const tiers = [...new Set(materials.map(m => m.budget_tier).filter(Boolean))];
      setAvailableBudgetTiers(tiers);
    }
  }, [materials]);

  // When material type changes, compute available budget tiers for that type
  const getAvailableBudgetTiersForType = () => {
    if (!materialType) return [];
    const typeMaterials = materials.filter(m => (m.material_type || 'granite') === materialType);
    return [...new Set(typeMaterials.map(m => m.budget_tier).filter(Boolean))];
  };

  // Helper function to get material image (supports both images array and legacy image_data)
  const getMaterialImage = (material) => {
    // First check for images array (new format)
    if (material.images && material.images.length > 0) {
      return material.images[0]; // Return first image URL
    }
    // Fall back to legacy image_data (base64)
    if (material.image_data) {
      return `data:image/jpeg;base64,${material.image_data}`;
    }
    return null;
  };

  const fetchSettings = async () => {
    try {
      const slug = companySlug || 'set-in-stone';
      const response = await axios.get(`${API}/public/company/${slug}/worktop-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  React.useEffect(() => {
    if (quoteType === 'instant' && selectedMaterial) {
      calculateInstantPrice();
    }
  }, [quoteType, selectedMaterial, pieces]);

  const calculateInstantPrice = () => {
    if (!selectedMaterial || !settings) return;
    
    let totalArea = 0;
    pieces.forEach(piece => {
      if (piece.width_mm && piece.height_mm) {
        const area = (parseFloat(piece.width_mm) * parseFloat(piece.height_mm)) / 1000000; // Convert to m²
        totalArea += area;
      }
    });

    // Apply retail multiplier to cost price
    const retailMultiplier = settings.retail_multiplier || 3.1;
    const retailPricePerM2 = selectedMaterial.price_per_m2 * retailMultiplier;
    const price = totalArea * retailPricePerM2;
    setInstantPrice(price);
  };

  const addPiece = () => {
    setPieces([...pieces, {
      name: `Worktop ${pieces.length + 1}`,
      width_mm: '',
      height_mm: '',
      material: 'Quartz',
      edge_profile: 'Square Edge',
      edges: { front: false, left: false, right: false, back: false }
    }]);
  };

  const removePiece = (index) => {
    if (pieces.length > 1) {
      setPieces(pieces.filter((_, i) => i !== index));
    }
  };

  const updatePiece = (index, field, value) => {
    const updated = [...pieces];
    updated[index][field] = value;
    setPieces(updated);
  };

  const calculateFullQuote = () => {
    if (!selectedMaterial || !settings) return null;

    // Calculate total area
    let totalArea = 0;
    pieces.forEach(piece => {
      if (piece.width_mm && piece.height_mm) {
        const area = (parseFloat(piece.width_mm) * parseFloat(piece.height_mm)) / 1000000;
        totalArea += area;
      }
    });

    // Material cost (retail price)
    const retailMultiplier = settings.retail_multiplier || 3.1;
    const retailPricePerM2 = selectedMaterial.price_per_m2 * retailMultiplier;
    const materialCost = totalArea * retailPricePerM2;

    // Extras costs (from settings)
    let extrasCost = 0;
    if (extras.undermount_sink) extrasCost += settings.undermount_sink_cost || 50;
    if (extras.overmount_sink) extrasCost += settings.overmount_sink_cost || 30;
    if (extras.hob_cutout) extrasCost += settings.hob_cutout_cost || 40;
    if (extras.tap_hole) extrasCost += settings.tap_hole_cost || 15;
    if (extras.drainer_grooves) extrasCost += settings.drainer_grooves_cost || 60;
    if (extras.socket_cutout) {
      const qty = parseInt(extras.socket_cutout_quantity) || 1;
      extrasCost += (settings.socket_cutout_cost || 20) * qty;
    }

    // Service costs
    let serviceCost = 0;
    if (serviceOptions.include_templating) serviceCost += settings.templating_cost || 100;
    if (serviceOptions.include_fitting) serviceCost += settings.fitting_cost || 200;

    // Subtotal
    const subtotal = materialCost + extrasCost + serviceCost;

    // VAT (20%)
    const vatRate = 20;
    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;

    return {
      materialCost,
      extrasCost,
      serviceCost,
      subtotal,
      vatAmount,
      total,
      totalArea
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate at least one piece with dimensions
      const validPieces = pieces.filter(p => p.width_mm && p.height_mm);
      if (validPieces.length === 0) {
        toast.error('Please add at least one worktop piece with dimensions');
        setLoading(false);
        return;
      }

      // Build the payload
      const payload = {
        ...formData,
        pieces: validPieces.map(p => ({
          name: p.name || 'Worktop',
          width_mm: parseFloat(p.width_mm),
          height_mm: parseFloat(p.height_mm),
          thickness: globalThickness,
          material: quoteType === 'instant' ? selectedMaterial.name : p.material,
          edge_profile: p.edge_profile,
          edges: {
            top: p.edges.front || false,
            right: p.edges.right || false,
            bottom: p.edges.back || false,
            left: p.edges.left || false
          }
        })),
        service_options: serviceOptions,
        extras: {
          ...extras,
          socket_cutout_quantity: parseInt(extras.socket_cutout_quantity) || 1
        },
        quote_type: quoteType
      };

      // For instant quotes, add calculated pricing
      if (quoteType === 'instant' && selectedMaterial) {
        const quote = calculateFullQuote();
        payload.material_name = selectedMaterial.name;
        payload.material_id = selectedMaterial.id;
        payload.budget_tier = budgetTier;
        payload.calculated_price = quote.total;
        payload.price_breakdown = {
          material_cost: quote.materialCost,
          extras_cost: quote.extrasCost,
          service_cost: quote.serviceCost,
          subtotal: quote.subtotal,
          vat_amount: quote.vatAmount,
          total: quote.total,
          total_area: quote.totalArea
        };
        payload.supplier = 'Instant Quote';
        payload.colour_name = selectedMaterial.name;
        payload.pm2_cost = selectedMaterial.price_per_m2;
      }
      
      // Add company slug to payload
      payload.company_slug = companySlug || 'set-in-stone';

      // Submit to backend
      const response = await axios.post(`${API}/worktops/public-quote-request`, payload);
      
      setSubmitted(true);
      setSubmittedQuoteId(response.data.id || response.data.quote_id);
      
      if (quoteType === 'instant') {
        toast.success('Quote calculated and submitted successfully!');
      } else {
        toast.success('Quote request submitted successfully! We will contact you soon.');
      }
    } catch (error) {
      console.error('Quote request error:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to submit quote request';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setRegisterLoading(true);
    
    try {
      // Register user with quote data
      const registerPayload = {
        email: formData.client_email,
        password: registerData.password,
        name: formData.client_name,
        role: 'client',
        address: formData.client_address
      };
      
      const response = await axios.post(`${API}/auth/register`, registerPayload);
      
      toast.success('Account created successfully! You can now login to view your quotes.');
      setShowRegisterDialog(false);
      
      // Optional: Auto-login the user
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to create account';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setRegisterLoading(false);
    }
  };

  if (submitted) {
    // For instant quotes, show full calculated quote
    if (quoteType === 'instant' && selectedMaterial) {
      const quote = calculateFullQuote();
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="mb-6 bg-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>

            <Card className="shadow-2xl mb-6">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Your Instant Quote</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">Calculated in real-time based on your selections</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Customer Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-3">Customer Details</h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-600">Name:</span> <span className="font-medium">{formData.client_name}</span></div>
                    <div><span className="text-gray-600">Email:</span> <span className="font-medium">{formData.client_email}</span></div>
                    <div><span className="text-gray-600">Phone:</span> <span className="font-medium">{formData.client_phone}</span></div>
                    <div><span className="text-gray-600">Address:</span> <span className="font-medium">{formData.client_address}</span></div>
                  </div>
                </div>

                {/* Material Selection */}
                <div className="border-b pb-4">
                  <h3 className="font-semibold text-lg mb-3">Selected Material</h3>
                  <div className="flex items-center gap-4">
                    {getMaterialImage(selectedMaterial) && (
                      <img
                        src={getMaterialImage(selectedMaterial)}
                        alt={selectedMaterial.name}
                        className="w-20 h-20 object-cover rounded border"
                      />
                    )}
                    <div>
                      <p className="font-semibold text-lg">{selectedMaterial.name}</p>
                      <p className="text-sm text-gray-600">£{(selectedMaterial.price_per_m2 * (settings.retail_multiplier || 3.1)).toFixed(2)}/m²</p>
                      <p className="text-sm text-gray-500">Total Area: {quote.totalArea.toFixed(2)}m²</p>
                    </div>
                  </div>
                </div>

                {/* Worktop Pieces */}
                <div className="border-b pb-4">
                  <h3 className="font-semibold text-lg mb-3">Worktop Pieces</h3>
                  <div className="space-y-2">
                    {pieces.filter(p => p.width_mm && p.height_mm).map((piece, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-gray-50 p-3 rounded">
                        <span>{piece.name || `Piece ${idx + 1}`}</span>
                        <span className="text-gray-600">{piece.width_mm}mm × {piece.height_mm}mm ({globalThickness})</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-sm text-gray-600">
                    <strong>Thickness:</strong> {globalThickness}
                  </div>
                </div>

                {/* Optional Extras */}
                {quote.extrasCost > 0 && (
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-lg mb-3">Optional Extras</h3>
                    <div className="space-y-2 text-sm">
                      {extras.undermount_sink && <div className="flex justify-between"><span>Undermount Sink</span><span>£{settings.undermount_sink_cost || 50}</span></div>}
                      {extras.overmount_sink && <div className="flex justify-between"><span>Overmount Sink</span><span>£{settings.overmount_sink_cost || 30}</span></div>}
                      {extras.hob_cutout && <div className="flex justify-between"><span>Hob Cutout</span><span>£{settings.hob_cutout_cost || 40}</span></div>}
                      {extras.tap_hole && <div className="flex justify-between"><span>Tap Hole</span><span>£{settings.tap_hole_cost || 15}</span></div>}
                      {extras.drainer_grooves && <div className="flex justify-between"><span>Drainer Grooves</span><span>£{settings.drainer_grooves_cost || 60}</span></div>}
                      {extras.socket_cutout && (
                        <div className="flex justify-between">
                          <span>Socket Cutout × {extras.socket_cutout_quantity}</span>
                          <span>£{((settings.socket_cutout_cost || 20) * parseInt(extras.socket_cutout_quantity || 1))}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Services */}
                {quote.serviceCost > 0 && (
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-lg mb-3">Services</h3>
                    <div className="space-y-2 text-sm">
                      {serviceOptions.include_templating && <div className="flex justify-between"><span>Professional Templating</span><span>£{settings.templating_cost || 100}</span></div>}
                      {serviceOptions.include_fitting && <div className="flex justify-between"><span>Professional Fitting</span><span>£{settings.fitting_cost || 200}</span></div>}
                    </div>
                  </div>
                )}

                {/* Price Breakdown */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6">
                  <h3 className="font-semibold text-lg mb-4">Price Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Material Cost ({quote.totalArea.toFixed(2)}m²)</span>
                      <span className="font-medium">£{quote.materialCost.toFixed(2)}</span>
                    </div>
                    {quote.extrasCost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Optional Extras</span>
                        <span className="font-medium">£{quote.extrasCost.toFixed(2)}</span>
                      </div>
                    )}
                    {quote.serviceCost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Services</span>
                        <span className="font-medium">£{quote.serviceCost.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm border-t pt-3">
                      <span>Subtotal</span>
                      <span className="font-medium">£{quote.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>VAT (20%)</span>
                      <span className="font-medium">£{quote.vatAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-2xl font-bold border-t-2 pt-4">
                      <span>Total</span>
                      <span className="text-blue-600">£{quote.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 pt-4">
                  <Button 
                    onClick={() => setShowRegisterDialog(true)} 
                    className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Create Account & Save Quote
                  </Button>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      onClick={() => window.print()} 
                      variant="outline"
                      className="flex-1"
                    >
                      Print Quote
                    </Button>
                    <Button 
                      onClick={() => {
                        setSubmitted(false);
                        setSubmittedQuoteId(null);
                        setInstantPrice(0);
                        setPieces([{
                          id: 1,
                          name: '',
                          width_mm: '',
                          height_mm: '',
                          thickness: globalThickness,
                          edges: { top: false, right: false, bottom: false, left: false }
                        }]);
                        setExtras({
                          undermount_sink: false,
                          overmount_sink: false,
                          hob_cutout: false,
                          socket_cutout: false,
                          socket_cutout_quantity: '1',
                          popup_socket: false,
                          tap_hole: false,
                          radius_corner: false,
                          large_rounded_corner: false,
                          drainer_grooves: false,
                          recessed_drainer: false
                        });
                        setServiceOptions({ include_templating: false, include_fitting: false });
                        // Keep formData (customer info) intact
                      }} 
                      variant="outline"
                      className="flex-1"
                    >
                      Get Another Quote
                    </Button>
                  </div>
                </div>

                <div className="text-center text-sm text-gray-600 mt-4 p-4 bg-yellow-50 rounded border border-yellow-200">
                  <p className="font-semibold text-gray-900 mb-1">Please Note:</p>
                  <p>This is an estimated quote. Final pricing may vary based on site survey and specific requirements. Contact us to confirm and proceed with your order.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    // For 24-hour quotes, show thank you message
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-lg text-gray-600 mb-2">
                Your quote request has been received successfully.
              </p>
              <p className="text-gray-600 mb-8">
                We'll review your requirements and get back to you within 24 hours with a detailed quote.
              </p>
              <Button 
                onClick={() => {
                  setSubmitted(false);
                  setSubmittedQuoteId(null);
                  // Keep formData (customer info) intact
                }} 
                className="bg-blue-600 hover:bg-blue-700"
              >
                Submit Another Request
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mb-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/')}
            className="bg-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => window.open('https://worktops.setinstonedidcot.co.uk/', '_blank')}
            className="bg-white"
          >
            Visit Our Website
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          {companyInfo?.branding?.logo_url || companyInfo?.branding?.logo_data ? (
            <img 
              src={companyInfo.branding.logo_url || companyInfo.branding.logo_data}
              alt={companyInfo?.name || 'Company Logo'}
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : null}
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {companyInfo?.name ? `${companyInfo.name}` : 'Request a Worktop Quote'}
          </h1>
          <p className="text-lg text-gray-600">
            {companyInfo?.branding?.company_tagline || 'Get a free, no-obligation quote for your kitchen worktop project'}
          </p>
          {companyInfo?.phone && (
            <p className="text-md text-gray-500 mt-2">
              Call us: <a href={`tel:${companyInfo.phone}`} className="text-blue-600 hover:underline">{companyInfo.phone}</a>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Quote Type Selection */}
          <Card className="mb-6 shadow-lg border-2 border-blue-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardTitle className="text-xl">Choose Your Quote Type</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setQuoteType('instant')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    quoteType === 'instant'
                      ? 'border-green-500 bg-green-50 shadow-lg'
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">⚡</div>
                    <h3 className="font-bold text-lg mb-2">Instant Online Quote</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Choose from our limited selection of popular colors
                    </p>
                    <div className="text-green-600 font-semibold">Get pricing immediately!</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setQuoteType('24hour')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    quoteType === '24hour'
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">📋</div>
                    <h3 className="font-bold text-lg mb-2">24-Hour Quote</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Any brand, color, or custom specification
                    </p>
                    <div className="text-blue-600 font-semibold">Response within 24 hours</div>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Contact Details */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-xl">Your Contact Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    placeholder="07123 456789"
                    required
                  />
                </div>
                <div>
                  <Label>Full Address *</Label>
                  <Input
                    value={formData.client_address}
                    onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                    placeholder="123 Main Street, City, Postcode"
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stone Details */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-xl">
                {quoteType === 'instant' ? 'Select Material' : 'Stone Preference'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {quoteType === 'instant' ? (
                <div>
                  {/* Material Type Selection */}
                  <div className="mb-6">
                    <Label className="text-lg font-semibold mb-3 block">What Material Type? *</Label>
                    <p className="text-sm text-gray-600 mb-4">Choose between granite, quartz, marble, or other stone types</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['granite', 'quartz', 'marble', 'quartzite', 'porcelain', 'other']
                        .filter(type => availableMaterialTypes.includes(type))
                        .map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => {
                            setMaterialType(type);
                            setBudgetTier(''); // Reset budget tier when material type changes
                          }}
                          className={`p-3 rounded-lg border-2 transition-all capitalize ${
                            materialType === type
                              ? 'border-blue-600 bg-blue-50 shadow-md'
                              : 'border-gray-300 hover:border-blue-300'
                          }`}
                        >
                          <div className="text-center">
                            <h4 className="font-semibold text-sm">{type}</h4>
                          </div>
                        </button>
                      ))}
                      {availableMaterialTypes.length === 0 && (
                        <p className="col-span-full text-center text-gray-500 py-4">No materials available. Please check back later.</p>
                      )}
                    </div>
                  </div>

                  {/* Budget Tier Selection */}
                  {materialType && (
                  <div className="mb-6">
                    <Label className="text-lg font-semibold mb-3 block">What's Your Budget? *</Label>
                    <p className="text-sm text-gray-600 mb-4">Select your budget range to see materials that fit your needs</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {getAvailableBudgetTiersForType().includes('best-price') && (
                      <button
                        type="button"
                        onClick={() => setBudgetTier('best-price')}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          budgetTier === 'best-price'
                            ? 'border-green-500 bg-green-50 shadow-md'
                            : 'border-gray-300 hover:border-green-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="flex justify-center mb-2">
                            <Wallet className="w-8 h-8 text-green-600" />
                          </div>
                          <h4 className="font-bold text-md">Budget Friendly</h4>
                          <p className="text-xs text-gray-600 mt-1">Best value for money</p>
                        </div>
                      </button>
                      )}

                      {getAvailableBudgetTiersForType().includes('mid') && (
                      <button
                        type="button"
                        onClick={() => setBudgetTier('mid')}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          budgetTier === 'mid'
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="flex justify-center mb-2">
                            <Award className="w-8 h-8 text-blue-600" />
                          </div>
                          <h4 className="font-bold text-md">Mid Range</h4>
                          <p className="text-xs text-gray-600 mt-1">Quality & value balance</p>
                        </div>
                      </button>
                      )}

                      {getAvailableBudgetTiersForType().includes('premium') && (
                      <button
                        type="button"
                        onClick={() => setBudgetTier('premium')}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          budgetTier === 'premium'
                            ? 'border-purple-500 bg-purple-50 shadow-md'
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="flex justify-center mb-2">
                            <Crown className="w-8 h-8 text-purple-600" />
                          </div>
                          <h4 className="font-bold text-md">Premium</h4>
                          <p className="text-xs text-gray-600 mt-1">Luxury materials</p>
                        </div>
                      </button>
                      )}
                      
                      {getAvailableBudgetTiersForType().length === 0 && (
                        <p className="col-span-full text-center text-gray-500 py-4">No budget tiers available for this material type.</p>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Material Selection */}
                  {materialType && budgetTier && (
                    <div>
                      <Label className="text-lg font-semibold mb-2 block">Choose Material *</Label>
                      {filteredMaterials.length === 0 ? (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-center">
                          <p className="text-gray-700">No materials available in this budget tier yet.</p>
                          <p className="text-sm text-gray-600 mt-1">Please try a different budget range or request a 24-hour quote.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {filteredMaterials.map((material) => {
                            const retailPrice = settings ? (material.price_per_m2 * (settings.retail_multiplier || 3.1)) : material.price_per_m2;
                            const isSelected = selectedMaterial?.id === material.id;
                            const materialImage = getMaterialImage(material);
                            
                            return (
                              <button
                                key={material.id}
                                type="button"
                                onClick={() => setSelectedMaterial(material)}
                                className={`p-4 rounded-lg border-2 transition-all text-left ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50 shadow-md'
                                    : 'border-gray-300 hover:border-blue-300'
                                }`}
                              >
                                {materialImage && (
                                  <img
                                    src={materialImage}
                                    alt={material.name}
                                    className="w-full h-32 object-cover rounded mb-3"
                                  />
                                )}
                                <h4 className="font-semibold text-lg">{material.name}</h4>
                                {material.color_code && (
                                  <p className="text-xs text-gray-500">Code: {material.color_code}</p>
                                )}
                                <div className="mt-2 text-lg font-bold text-blue-600">
                                  £{retailPrice.toFixed(2)}/m²
                                </div>
                                {material.description && (
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{material.description}</p>
                                )}
                                {isSelected && (
                                  <div className="mt-2 flex items-center gap-1 text-blue-600">
                                    <Check className="w-4 h-4" />
                                    <span className="text-sm font-semibold">Selected</span>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {instantPrice > 0 && selectedMaterial && (
                    <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Estimated Material Cost</p>
                        <p className="text-3xl font-bold text-blue-600">£{instantPrice.toFixed(2)}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          * Final price includes installation, templating, and any extras selected below
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <Label>Preferred Stone Colour/Type</Label>
                  <Input
                    value={formData.stone_colour}
                    onChange={(e) => setFormData({ ...formData, stone_colour: e.target.value })}
                    placeholder="e.g., White Quartz, Black Granite, any brand"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Tell us your preferred brand, color, or specifications - we'll source it for you
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Worktop Dimensions */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-xl">Worktop Dimensions</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 mb-4">Add each piece of worktop you need (e.g., one for each counter section)</p>
              
              {/* Global Thickness Selection */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <Label className="text-base font-semibold mb-2 block">Stone Thickness (applies to all pieces) *</Label>
                <select
                  value={globalThickness}
                  onChange={(e) => setGlobalThickness(e.target.value)}
                  className="w-full md:w-64 px-3 py-2 border rounded-md bg-white"
                  required
                >
                  <option value="20mm">20mm</option>
                  <option value="30mm">30mm</option>
                </select>
                <p className="text-xs text-gray-600 mt-2">All worktop pieces will have the same thickness</p>
              </div>
              
              {pieces.map((piece, index) => (
                <div key={index} className="border rounded-lg p-4 mb-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-700">Piece {index + 1}</h4>
                    {pieces.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePiece(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <Label>Piece Name *</Label>
                    <Input
                      value={piece.name}
                      onChange={(e) => updatePiece(index, 'name', e.target.value)}
                      placeholder="e.g., Main Worktop, Splashback, Upstand"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Width (left to right) *</Label>
                      <Input
                        type="number"
                        value={piece.width_mm}
                        onChange={(e) => updatePiece(index, 'width_mm', e.target.value)}
                        placeholder="e.g., 3000"
                        required
                      />
                    </div>
                    <div>
                      <Label>Depth (front to back) *</Label>
                      <Input
                        type="number"
                        value={piece.height_mm}
                        onChange={(e) => updatePiece(index, 'height_mm', e.target.value)}
                        placeholder="e.g., 600"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label>Material *</Label>
                      <select
                        value={piece.material}
                        onChange={(e) => updatePiece(index, 'material', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="Quartz">Quartz</option>
                        <option value="Granite">Granite</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <Label>Edge Profile *</Label>
                    <select
                      value={piece.edge_profile}
                      onChange={(e) => updatePiece(index, 'edge_profile', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="Square Edge">Square Edge</option>
                      <option value="Pencil Round">Pencil Round</option>
                      <option value="Half Bullnose">Half Bullnose</option>
                      <option value="Full Bullnose">Full Bullnose</option>
                      <option value="Ogee">Ogee</option>
                      <option value="Bevelled">Bevelled</option>
                    </select>
                  </div>
                  
                  <div className="mt-3">
                    <Label className="block mb-1">Finished Edges (Visible Edges) *</Label>
                    <p className="text-sm text-gray-600 mb-2">
                      Select the edges that will be visible after installation. Don't select edges that will be against walls or inside units.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {['front', 'left', 'right', 'back'].map((edge) => (
                        <button
                          key={edge}
                          type="button"
                          onClick={() => {
                            const updated = [...pieces];
                            updated[index].edges[edge] = !updated[index].edges[edge];
                            setPieces(updated);
                          }}
                          className={`px-4 py-2 rounded-md border-2 transition-all ${
                            piece.edges[edge]
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {edge.charAt(0).toUpperCase() + edge.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mt-4">
                <p className="text-sm text-gray-600">
                  <strong className="text-gray-900">Note about Upstands:</strong> If you require upstands, please add them as ONE long piece by combining all the lengths together. 
                  For example, if you need three 1000mm upstands, add them as one piece: 3000mm x 100mm (height).
                </p>
              </div>
              
              <Button
                type="button"
                variant="outline"
                onClick={addPiece}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Another Piece
              </Button>
            </CardContent>
          </Card>

          {/* Service Options */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-xl">Installation & Delivery</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include_templating"
                    checked={serviceOptions.include_templating}
                    onChange={(e) => setServiceOptions({ 
                      ...serviceOptions, 
                      include_templating: e.target.checked,
                      supply_only: false,
                      delivery_only: false
                    })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Label htmlFor="include_templating" className="ml-2 font-semibold">Include Templating</Label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include_fitting"
                    checked={serviceOptions.include_fitting}
                    onChange={(e) => setServiceOptions({ 
                      ...serviceOptions, 
                      include_fitting: e.target.checked,
                      supply_only: false,
                      delivery_only: false
                    })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Label htmlFor="include_fitting" className="ml-2 font-semibold">Include Professional Fitting</Label>
                </div>
                
                <div className="border-t pt-3 mt-3">
                  <p className="text-sm text-gray-600 mb-2">Or select alternative service:</p>
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id="supply_only"
                      checked={serviceOptions.supply_only}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setServiceOptions({ 
                            include_templating: false,
                            include_fitting: false,
                            supply_only: true,
                            delivery_only: false
                          });
                        } else {
                          setServiceOptions({ 
                            ...serviceOptions, 
                            supply_only: false
                          });
                        }
                      }}
                      className="w-4 h-4 text-blue-600"
                    />
                    <Label htmlFor="supply_only" className="ml-2">Supply Only (collection from our premises)</Label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="delivery_only"
                      checked={serviceOptions.delivery_only}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setServiceOptions({ 
                            include_templating: false,
                            include_fitting: false,
                            supply_only: false,
                            delivery_only: true
                          });
                        } else {
                          setServiceOptions({ 
                            ...serviceOptions, 
                            delivery_only: false
                          });
                        }
                      }}
                      className="w-4 h-4 text-blue-600"
                    />
                    <Label htmlFor="delivery_only" className="ml-2">Delivery Only (no installation)</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Extras & Cutouts */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-xl">Additional Requirements</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {/* Undermount Sink */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="undermount_sink"
                    checked={extras.undermount_sink}
                    onChange={(e) => setExtras({ ...extras, undermount_sink: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Label htmlFor="undermount_sink" className="ml-2 font-semibold">Undermount Sink Cutout</Label>
                </div>
                {extras.undermount_sink && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="text-sm">Width (left to right)</Label>
                      <Input
                        type="number"
                        value={extras.undermount_sink_width}
                        onChange={(e) => setExtras({ ...extras, undermount_sink_width: e.target.value })}
                        placeholder="e.g., 500"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Depth (front to back)</Label>
                      <Input
                        type="number"
                        value={extras.undermount_sink_height}
                        onChange={(e) => setExtras({ ...extras, undermount_sink_height: e.target.value })}
                        placeholder="e.g., 400"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Overmount Sink */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="overmount_sink"
                    checked={extras.overmount_sink}
                    onChange={(e) => setExtras({ ...extras, overmount_sink: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Label htmlFor="overmount_sink" className="ml-2 font-semibold">Overmount Sink Cutout</Label>
                </div>
                {extras.overmount_sink && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="text-sm">Width (left to right)</Label>
                      <Input
                        type="number"
                        value={extras.overmount_sink_width}
                        onChange={(e) => setExtras({ ...extras, overmount_sink_width: e.target.value })}
                        placeholder="e.g., 500"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Depth (front to back)</Label>
                      <Input
                        type="number"
                        value={extras.overmount_sink_height}
                        onChange={(e) => setExtras({ ...extras, overmount_sink_height: e.target.value })}
                        placeholder="e.g., 400"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Hob Cutout */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="hob_cutout"
                    checked={extras.hob_cutout}
                    onChange={(e) => setExtras({ ...extras, hob_cutout: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Label htmlFor="hob_cutout" className="ml-2 font-semibold">Hob Cutout</Label>
                </div>
                {extras.hob_cutout && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="text-sm">Width (left to right)</Label>
                      <Input
                        type="number"
                        value={extras.hob_cutout_width}
                        onChange={(e) => setExtras({ ...extras, hob_cutout_width: e.target.value })}
                        placeholder="e.g., 600"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Depth (front to back)</Label>
                      <Input
                        type="number"
                        value={extras.hob_cutout_height}
                        onChange={(e) => setExtras({ ...extras, hob_cutout_height: e.target.value })}
                        placeholder="e.g., 500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Tap Hole */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="tap_hole"
                    checked={extras.tap_hole}
                    onChange={(e) => setExtras({ ...extras, tap_hole: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Label htmlFor="tap_hole" className="ml-2 font-semibold">Tap Hole</Label>
                </div>
              </div>

              {/* Other Extras */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="drainer_grooves"
                    checked={extras.drainer_grooves}
                    onChange={(e) => setExtras({ ...extras, drainer_grooves: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Label htmlFor="drainer_grooves" className="ml-2 font-semibold">Drainer Grooves</Label>
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="socket_cutout"
                    checked={extras.socket_cutout}
                    onChange={(e) => setExtras({ ...extras, socket_cutout: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Label htmlFor="socket_cutout" className="ml-2 font-semibold">Socket/Plug Cutout</Label>
                </div>
                {extras.socket_cutout && (
                  <div className="mt-3">
                    <Label className="text-sm">Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={extras.socket_cutout_quantity}
                      onChange={(e) => setExtras({ ...extras, socket_cutout_quantity: e.target.value })}
                      placeholder="e.g., 2"
                      className="w-32"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Additional Notes */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-xl">Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Label className="text-base text-gray-700">Any other details or special requirements?</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="e.g., specific edge profile, installation date preferences, or any questions you have..."
                rows={4}
                className="mt-2 text-base text-gray-900"
              />
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
          >
            {loading ? 'Submitting...' : 'Get My Free Quote'}
          </Button>

          <p className="text-center text-sm text-gray-500 mt-4">
            By submitting this form, you agree to be contacted about your quote request
          </p>
        </form>

        {/* Registration Dialog */}
        <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Your Account</DialogTitle>
              <DialogDescription>
                Save your quote and access it anytime. We'll use the details you provided for your quote.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleRegister} className="space-y-4 mt-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={formData.client_name}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  placeholder="Create a password (min 6 characters)"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  placeholder="Confirm your password"
                  required
                  minLength={6}
                />
              </div>

              <div className="bg-blue-50 p-3 rounded text-sm">
                <p className="text-gray-700">
                  <strong>Benefits:</strong> Track your quote, receive updates, and access your quote history anytime.
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowRegisterDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={registerLoading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {registerLoading ? 'Creating...' : 'Create Account'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default PublicWorktopQuote;
